function isCanadaCountry(country) {
  if (!country) return true; // default to Canada
  const countryObj = !!country && typeof country.toJS === 'function' ? country.toJS() : country;
  const name = countryObj.label || countryObj.value || '';
  return name.toLowerCase() === 'canada';
}

function mapCanadaBankFields(data) {
  const bankBranchCode = data.institutionNumber
    ? `${data.institutionNumber || ''}${data.bsbNumber || ''}`
    : data.bsbNumber || '';
  return {
    bankAccountName: data.accountHolder,
    bankAccountNumber: data.accountNumber,
    bankBranchName: data.branch,
    bankBranchCode,
    bankName: data.nameOfBank,
    bankCodeType: 'Bank Code',
  };
}

function mapTrawickBankFields(data) {
  const countryDetails = !!data.country && typeof data.country.toJS === 'function' ? data.country.toJS() : data.country;
  return {
    bankName: data.bank,
    bankBranchCode: data.bankTypeCode === 'SWIFT Code' ? data.swiftCode : data.bankTypeCode === 'Bank Code' ? data.bankCodeOrRoutingNumber : '',
    bankBranchName: data.bankBranch,
    bankRoutingNumber: data.bankCodeOrRoutingNumber,
    bankAccountName: data.bankAccountName,
    bankAccountNumber: data.bankAccountNumber,
    bankCodeType: data.bankTypeCode,
    bankAddress: data.bankAddress,
    bankPostcode: data.bankPostcode,
    bankSuburbOrCity: data.bankSuburbOrCity,
    IBAN: data.bankTypeCode === 'IBAN' ? data.iban : null,
    fBankCountry: countryDetails ? countryDetails.id : null,
  };
}

function mapCheckFields(data) {
  return {
    creditorName: data.firstName,
    creditorSurname: data.lastName,
    creditorFullName: `${data.firstName || ''} ${data.lastName || ''}`.trim(),
    physicalAddress1: data.addressLine1,
    physicalAddress2: data.addressLine2,
    physicalAddress3: data.city,
    physicalAddress4: data.state,
    physicalCode: data.postalZipCode,
    titleID: data.title,
    cellNumber: data.mobile,
  };
}

/**
 * Maps dynamic payment form values (keyed by API FieldName) to the claim contract fields.
 * API FieldNames: BankBranchCode, BankBranchName, BankName, BankAddress, BankSuburb,
 *                 BankPostCode, BankAccountName, BankAccountNumber, IsInvalidBsb, SwiftCode, IBAN
 */
function mapDynamicBankFields(dynamicFormValues, dynamicPaymentFormFields) {
  if (!dynamicFormValues || Object.keys(dynamicFormValues).length === 0) {
    return {};
  }

  // Derive primary bank code type from the field the DB flags as IsCodeCheck (TA-4751/TA-4779) -
  // field presence alone can't tell SWIFT/IBAN/BankBranchCode apart since all three may coexist on a form.
  const fieldNames = (dynamicPaymentFormFields || []).map((f) => f.fieldName);
  const codeCheckField = (dynamicPaymentFormFields || []).find((f) => f.isCodeCheck === true);
  let bankCodeType = 'Bank Code';
  if (codeCheckField) {
    if (codeCheckField.fieldName === 'SwiftCode') bankCodeType = 'SWIFT Code';
    else if (codeCheckField.fieldName === 'IBAN') bankCodeType = 'IBAN';
    else if (codeCheckField.fieldName === 'BankBranchCode') bankCodeType = 'Bank Code';
  } else if (fieldNames.includes('SwiftCode')) bankCodeType = 'SWIFT Code';
  else if (fieldNames.includes('IBAN')) bankCodeType = 'IBAN';
  else if (fieldNames.includes('BankBranchCode')) bankCodeType = 'Bank Code';

  const swiftCode = dynamicFormValues.SwiftCode || '';
  const iban = dynamicFormValues.IBAN || '';
  const bankBranchCode = dynamicFormValues.BankBranchCode || '';

  let resolvedBankBranchCode = bankBranchCode;
  if (bankCodeType === 'SWIFT Code') resolvedBankBranchCode = swiftCode;
  else if (bankCodeType === 'IBAN') resolvedBankBranchCode = iban;

  return {
    bankName: dynamicFormValues.BankName || '',
    bankBranchCode: resolvedBankBranchCode,
    bankBranchName: dynamicFormValues.BankBranchName || '',
    bankRoutingNumber: bankBranchCode,
    bankAccountName: dynamicFormValues.BankAccountName || '',
    bankAccountNumber: dynamicFormValues.BankAccountNumber || '',
    ...(fieldNames.includes('BankAccountType') && { bankAccountType: dynamicFormValues.BankAccountType === undefined ? null : dynamicFormValues.BankAccountType }),
    bankCodeType,
    bankAddress: dynamicFormValues.BankAddress || '',
    bankPostcode: dynamicFormValues.BankPostCode || '',
    bankSuburbOrCity: dynamicFormValues.BankSuburb || '',
    IBAN: bankCodeType === 'IBAN' ? iban : null,
    swiftCode: swiftCode || null,
    recipientReference: dynamicFormValues.RecipientReference || null,
    identificationNumber: dynamicFormValues.IdentificationNumber || null,
    paymentCode: dynamicFormValues.PaymentCode || null,
    invalidBSB: !!dynamicFormValues.IsInvalidBsb,
  };
}

function mapToParentSection(data) {
  const countryDetails = !!data.country && typeof data.country.toJS === 'function' ? data.country.toJS() : data.country;
  const isCanada = isCanadaCountry(data.country);
  const isCheckPayment = data.isCheckPayment === true;
  // Use dynamic form mapping when dynamicPaymentFormFields are loaded (DYNAMIC_PAYMENT_FORM=true).
  // When DYNAMIC_PAYMENT_FORM=false, dynamicPaymentFormFields stays empty → falls back to old logic.
  const hasDynamicForm = data.dynamicPaymentFormFields && data.dynamicPaymentFormFields.length > 0;

  // Base fields (GST, other insurance - always included)
  const baseFields = {
    ABNNumber: data.abn,
    gstRegistered: data.registerForGSTPurpose,
    gstPercentageClaimed: data.percentageOfGST,
    claimInputTaxCredit: data.claimITC,
    country: countryDetails ? countryDetails.id : null,
    creditorExtension: {
      payToEntity: data.paymentTo,
      paymentVia: data.paymentVia || 'EFT',
    },
  };

  // Bank fields depend on whether dynamic form, static Canada, static Trawick, or check payment
  let bankFields = {};
  if (!isCheckPayment) {
    if (isCanada) {
      bankFields = mapCanadaBankFields(data);
    } else if (hasDynamicForm) {
      bankFields = mapDynamicBankFields(data.dynamicFormValues, data.dynamicPaymentFormFields);
    } else {
      bankFields = mapTrawickBankFields(data);
    }
  }

  // Check/address fields when check payment is selected
  const checkFields = isCheckPayment ? mapCheckFields(data) : {};

  return {
    ...baseFields,
    ...bankFields,
    ...checkFields,
  };
}

function mapToQuestionnaire(data, claimCode) {
  return [
    {
      question: 'section1.otherInsurance',
      answer: data.otherInsurance,
      claimTypeCode: claimCode,
    },
    {
      question: 'section1.provideDetails',
      answer: data.provideDetails,
      claimTypeCode: claimCode,
    },
  ];
}

function mapToExpenses() {
  return [];
}

export default {
  mapToParentSection,
  mapToQuestionnaire,
  mapToExpenses,
};



// WEBPACK FOOTER //
// ./app/containers/FastCover/dataMapper/stepAppsureBankDetailsDataMapper.js